/** @file puppet.setProfile: changes the puppet's profile and its overrides (z scales 17.7, tilePx 22.5). */
import type { ProfileOverrides } from '@zilo/pose3d-format/types/profile';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkTilePx } from '@zilo/pose3d-format/validate/fields/checkTilePx';
import type { CommandDef } from '../apply/commandDef';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { requireProfile } from '../validate/requireProfile';

const positive = checkNumber({ above: 0 });

function readOverrides(value: unknown, path: string): ProfileOverrides {
  const r = readInput(value, path, ['zScaleRotate', 'zScaleView', 'tilePx']);
  const scale = (k: string): number => {
    positive(r.raw(k), r.at(k));
    return r.raw(k) as number;
  };
  const tilePx = (k: string): [number, number, number] => [...(r.check(k, checkTilePx) as [number, number, number])];
  return compact({ zScaleRotate: r.opt('zScaleRotate', scale), zScaleView: r.opt('zScaleView', scale), tilePx: r.opt('tilePx', tilePx) });
}

/**
 * `puppet.setProfile { profile, overrides? }` → `{ profile }`: `overrides` replaces `profileOverrides` (`null`
 * removes it, absent keeps it). Inverse: the previous profile and overrides (`null` when there were none).
 */
export const puppetSetProfileCommand: CommandDef<'puppet.setProfile'> = {
  name: 'puppet.setProfile',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['profile', 'overrides']);
    const overrides = r.opt('overrides', (k) => r.nullable(k, () => readOverrides(r.raw(k), r.at(k))));
    return compact({ profile: r.str('profile'), overrides });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const inverse = [{ name: 'puppet.setProfile' as const, input: { profile: p.profile, overrides: p.profileOverrides ?? null } }];
    p.profile = requireProfile(input.profile, ctx);
    if (input.overrides === null) delete p.profileOverrides;
    else if (input.overrides !== undefined) p.profileOverrides = { ...input.overrides };
    checkPuppet(draft, ctx);
    return { result: { profile: p.profile }, warnings: [], inverse };
  },
};
